import React from 'react';
import Icon from '../../../components/AppIcon';

const MarketOverview = ({ selectedCrypto, onCryptoSelect }) => {
  const marketData = [
    {
      symbol: 'BTC/USD',
      name: 'Bitcoin',
      price: 44750.00,
      change: 2.45,
      changePercent: 5.79,
      volume: '28.5B',
      marketCap: '876.2B',
      icon: 'Bitcoin',
      trend: 'up'
    },
    {
      symbol: 'ETH/USD',
      name: 'Ethereum',
      price: 2650.30,
      change: -45.20,
      changePercent: -1.68,
      volume: '15.2B',
      marketCap: '318.4B',
      icon: 'Ethereum',
      trend: 'down'
    },
    {
      symbol: 'BNB/USD',
      name: 'BNB',
      price: 315.80,
      change: 8.90,
      changePercent: 2.90,
      volume: '2.1B',
      marketCap: '47.3B',
      icon: 'Coins',
      trend: 'up'
    },
    {
      symbol: 'ADA/USD',
      name: 'Cardano',
      price: 0.485,
      change: 0.012,
      changePercent: 2.54,
      volume: '890M',
      marketCap: '17.2B',
      icon: 'Heart',
      trend: 'up'
    },
    {
      symbol: 'SOL/USD',
      name: 'Solana',
      price: 98.45,
      change: -2.15,
      changePercent: -2.14,
      volume: '1.8B',
      marketCap: '42.8B',
      icon: 'Sun',
      trend: 'down'
    },
    {
      symbol: 'DOT/USD',
      name: 'Polkadot',
      price: 7.23,
      change: 0.18,
      changePercent: 2.55,
      volume: '456M',
      marketCap: '9.1B',
      icon: 'Circle',
      trend: 'up'
    }
  ];

  const marketStats = {
    totalMarketCap: '1.72T',
    totalVolume: '89.2B',
    btcDominance: 50.8,
    fearGreedIndex: 72,
    activeCoins: 2847
  };

  const formatPrice = (price) => {
    if (price < 1) {
      return `$${price?.toFixed(4)}`;
    }
    return `$${price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatChange = (change, percent) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change?.toFixed(2)} (${sign}${percent?.toFixed(2)}%)`;
  };

  return (
    <div className="space-y-4">
      {/* Market Statistics */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Globe" size={20} />
          <span>Market Overview</span>
        </h3>
        
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Market Cap</p>
            <p className="text-lg font-semibold text-foreground">${marketStats?.totalMarketCap}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">24h Volume</p>
            <p className="text-lg font-semibold text-foreground">${marketStats?.totalVolume}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">BTC Dominance</p>
            <p className="text-lg font-semibold text-foreground">{marketStats?.btcDominance}%</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Fear & Greed</p>
            <p className="text-lg font-semibold text-success">{marketStats?.fearGreedIndex}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">Active Coins</p>
            <p className="text-lg font-semibold text-foreground">{marketStats?.activeCoins}</p>
          </div>
        </div>
      </div>
      {/* Cryptocurrency List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="TrendingUp" size={20} />
            <span>Top Cryptocurrencies</span>
          </h3>
        </div>
        
        <div className="divide-y divide-border">
          {marketData?.map((crypto) => (
            <div
              key={crypto?.symbol}
              className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                selectedCrypto === crypto?.symbol ? 'bg-primary/10 border-l-4 border-l-primary' : ''
              }`}
              onClick={() => onCryptoSelect(crypto?.symbol)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name={crypto?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{crypto?.symbol}</h4>
                    <p className="text-xs text-muted-foreground">{crypto?.name}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-sm font-semibold text-foreground">{formatPrice(crypto?.price)}</p>
                  <p className={`text-xs ${crypto?.change >= 0 ? 'text-success' : 'text-error'}`}>
                    {formatChange(crypto?.change, crypto?.changePercent)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                <div className="flex items-center space-x-4">
                  <span>Vol: {crypto?.volume}</span>
                  <span>MCap: {crypto?.marketCap}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon 
                    name={crypto?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                    size={14} 
                    className={crypto?.trend === 'up' ? 'text-success' : 'text-error'} 
                  />
                  <span className={crypto?.trend === 'up' ? 'text-success' : 'text-error'}>
                    {crypto?.trend === 'up' ? 'Bullish' : 'Bearish'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Analysis */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="Zap" size={16} />
          <span>Quick Analysis</span>
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-2 bg-success/10 border border-success/20 rounded">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingUp" size={14} className="text-success" />
              <span className="text-xs font-medium text-foreground">Overall Trend</span>
            </div>
            <span className="text-xs text-success font-semibold">Bullish</span>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-warning/10 border border-warning/20 rounded">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={14} className="text-warning" />
              <span className="text-xs font-medium text-foreground">Volatility</span>
            </div>
            <span className="text-xs text-warning font-semibold">High</span>
          </div>
          
          <div className="flex items-center justify-between p-2 bg-accent/10 border border-accent/20 rounded">
            <div className="flex items-center space-x-2">
              <Icon name="Activity" size={14} className="text-accent" />
              <span className="text-xs font-medium text-foreground">Market Sentiment</span>
            </div>
            <span className="text-xs text-accent font-semibold">Greed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketOverview;